import { useCallback, useMemo } from 'react';
import { useSupportsHover } from '../../../../../hooks/useSupportsHover';
import { BynderFormats, getImageUrl } from '../../../../../utils/bynder-utils';
import type { SearchVariant } from '../../../hooks/use-search/use-search-types';
import type { ImageFetchPriorityType, ImageLoadingType } from '../../product-image';
import type { SelectedVariant } from '../hardgood/ProductTileHardGood';

interface ProductImageThumbnail {
    id: string;
    src: string;
    alt: string;
    loading: ImageLoadingType;
    fetchPriority: ImageFetchPriorityType;
}

interface UseProductImagesProps {
    id: string;
    name: string;
    c_imageUrl: string;
    c_variants: SearchVariant[];
    selection?: SelectedVariant;
    loading?: ImageLoadingType;
    fetchPriority?: ImageFetchPriorityType;
    baseUrl?: string;
}

export const useProductImages = ({
    id,
    name,
    c_imageUrl,
    c_variants,
    selection,
    loading = 'eager',
    fetchPriority = 'high',
    baseUrl,
}: UseProductImagesProps): {
    thumbnail: ProductImageThumbnail;
    carouselImages: ProductImageThumbnail[];
} => {
    const supportsHover = useSupportsHover();

    // Extracts the folder ID from a Sur La Table image URL
    const getWebpMediumUrl = useCallback((url: string): string | null => {
        // Convert the url to the webp_medium format
        const standardizedUrl = getImageUrl(url, BynderFormats.WEBP_MEDIUM);
        if (!standardizedUrl) return null;
        return standardizedUrl;
    }, []);

    // Reorders sku_images array if the first image has the same folder ID as c_imageUrl
    const reorderSkuImagesIfNeeded = useCallback(
        (skuImages: string[], cImageUrl: string, isNotMobile: boolean): string[] => {
            if (!skuImages.length || !cImageUrl) return skuImages;

            const firstImageFolderId = getWebpMediumUrl(skuImages[0]);
            const cImageFolderId = getWebpMediumUrl(cImageUrl);

            if (firstImageFolderId && cImageFolderId && firstImageFolderId === cImageFolderId) {
                if (isNotMobile) {
                    // For desktop, move first item to last position
                    return [...skuImages.slice(1), skuImages[0]];
                }

                // For mobile, discard the duplicate image (first item)
                return skuImages.slice(1);
            }

            return skuImages;
        },
        [getWebpMediumUrl],
    );

    // Maps sku_images array to ProductImageThumbnail array with proper loading and fetchPriority
    const mapSkuImagesToThumbnails = useCallback(
        (images: string[], variantId: string): ProductImageThumbnail[] =>
            images.slice(0, 5).map((src: string, idx: number) => ({
                id: `${variantId}-${idx}`,
                src,
                alt: name,
                loading: idx === 0 ? 'eager' : 'lazy',
                fetchPriority: ((): ImageFetchPriorityType => {
                    if (idx <= 1) return 'auto';
                    return 'low';
                })(),
            })),
        [name],
    );

    const thumbnail = useMemo(() => {
        const src = baseUrl ?? selection?.c_imageUrl ?? c_imageUrl;
        return {
            id: selection?.id ?? id,
            src,
            alt: name,
            loading: loading ?? 'eager',
            fetchPriority: fetchPriority ?? 'high',
        };
    }, [
        id,
        selection?.id,
        selection?.c_imageUrl,
        c_imageUrl,
        name,
        loading,
        fetchPriority,
        baseUrl,
    ]);

    // SLTBE-6463 show up to 5 images
    // First image (index 0): loading="eager" and fetchPriority="auto"
    // Second image (index 1): loading="lazy" and fetchPriority="auto" - loads next
    // Remaining images: loading="lazy" and fetchPriority="low" - loads last
    const carouselImages = useMemo((): ProductImageThumbnail[] => {
        let baseImages: ProductImageThumbnail[] = [];

        // Use selection sku_images if available
        if (selection?.sku_images?.length) {
            const reorderedImages = reorderSkuImagesIfNeeded(
                selection.sku_images,
                selection.c_imageUrl,
                supportsHover,
            );
            baseImages = mapSkuImagesToThumbnails(reorderedImages, selection.id);
        } else {
            // Fallback to first variant sku_images
            const firstVariant = c_variants?.[0];
            if (firstVariant?.sku_images && firstVariant.sku_images.length > 0) {
                const reorderedImages = reorderSkuImagesIfNeeded(
                    firstVariant.sku_images,
                    firstVariant.c_imageUrl,
                    supportsHover,
                );
                baseImages = mapSkuImagesToThumbnails(reorderedImages, id);
            }
        }

        // If no hover support, prepend thumbnail and ensure 6 images
        if (!supportsHover) {
            return [thumbnail, ...baseImages.slice(0, 6)];
        }

        return baseImages;
    }, [
        supportsHover,
        thumbnail,
        selection,
        id,
        c_variants,
        mapSkuImagesToThumbnails,
        reorderSkuImagesIfNeeded,
    ]);

    return {
        thumbnail,
        carouselImages,
    };
};
